import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Range, DateRangePicker } from "react-date-range";
import DataTable, { ColumnType } from "../../../common/components/xepp/table/DataTable";
import { TablePagination } from "../../../common/components/xepp/table-pagination/TablePagination";
import EmptyState from "../../../common/components/xepp/empty-state/EmptyState";
import Button from "../../../common/components/xepp/button/Button";
import CommonDialog from "../../../common/components/dialog/CommonDialog";
import { FilterIcon2, SearchIcon } from "../../../assets/icons";
import { SystemReferralMenuIconActive } from "../../../assets/sidemenu-icons";
import {  StaffReferral } from "../../../model/referral/referral";
import { formatTimestamp, formatCurrency } from "../../../utils";
import styles from "./ReferralSystem.module.scss";

import { useGetStaffReferralsQuery } from "../../../service/referral-service";
import CommonLoadingSpinner from "../../../common/components/loading-spinner/CommonLoadingSpinnerForPage";


const PAGE_SIZE = 10;

export const ReferralSystemList = () => {
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [dateRange, setDateRange] = useState<Range>({
        startDate: undefined,
        endDate: undefined,
        key: "selection",
    });
    const [appliedFilters, setAppliedFilters] = useState<{
        from?: Date;
        to?: Date;
    }>({});

    const toStartOfDay = (date: Date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}T00:00:00`;
    };

    const toEndOfDay = (date: Date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}T23:59:59`;
    };

    const { data, isLoading, isFetching, isError, refetch } = useGetStaffReferralsQuery({
        page,
        pageSize: PAGE_SIZE,
        search: search || undefined,
        fromDate: appliedFilters.from ? toStartOfDay(appliedFilters.from) : undefined,
        toDate: appliedFilters.to ? toEndOfDay(appliedFilters.to) : undefined,
    });

    const maxDate = new Date();


    const handleApplyFilter = () => {
        setAppliedFilters({
            from: dateRange.startDate,
            to: dateRange.endDate,
        });
        setIsFilterOpen(false);
        setPage(1);
        console.log(dateRange.startDate, dateRange.endDate)
    };

    const handleResetFilter = () => {
        setDateRange({ startDate: undefined, endDate: undefined, key: "selection" });
        setAppliedFilters({});
        setIsFilterOpen(false);
        setPage(1);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
        setPage(1);
    };

    const columns: Array<ColumnType<StaffReferral>> = [
        {
            accessor: "createdAt",
            header: "Date & Time",
            render: (record: StaffReferral) => <span>{formatTimestamp(record.created)}</span>,
        },
        {
            accessor: "staffName",
            header: "Name of Staff",
            render: (record: StaffReferral) => <span>{record.fullName}</span>,
        },
        {
            accessor: "email",
            header: "Email Address",
            render: (record: StaffReferral) => <span>{record.email}</span>,
        },
        {
            accessor: "staffId",
            header: "Staff ID",
            render: (record: StaffReferral) => <span>{record.staffId}</span>,
        },
        {
            accessor: "referralCode",
            header: "Referral Code",
            render: (record: StaffReferral) => <span>{record.referralCode}</span>,
        },
        {
            accessor: "signUpCount",
            header: "Sign Up Count",
            render: (record: StaffReferral) => <span>{record.numberOfRegisterReferrals}</span>,
        },
        {
            accessor: "totalRevenue",
            header: "Referral Transaction",
            render: (record: StaffReferral) => <span>{formatCurrency(record.totalRevenue ?? 0)}</span>,
        },
    ];

    const headerActions = (
        <div className={styles.headerActions}>
            <SearchIcon/>
            <input
                className={styles.searchInput}
                type="text"
                placeholder="Search by Name"
                value={search}
                onChange={handleSearchChange}
            />
            <div className={styles.filterActions}>
                 <span className={styles.filterStatus}>Date</span>
                {(appliedFilters.from || appliedFilters.to) && (
                    <>
                       
                        <Button text="Clear" variant="text" onClick={handleResetFilter} />
                    </>
                )}
                <Button
                    leftIcon={<FilterIcon2 />}
                    text="Add Filter"
                    variant="outline"
                    type="button"
                    onClick={() => setIsFilterOpen(true)}
                />
            </div>
        </div>
    );

    if ((isLoading || !data) && !isError ) return <CommonLoadingSpinner loading={true}>{null}</CommonLoadingSpinner>;

    if (isError) return (
        <div className={styles.errorContainer}>
            <p className={styles.error}>Something went wrong while loading referrals.</p>
            <Button text="Try again" className={styles.btn} onClick={refetch} />
        </div>
    );

    return (
        <>
            {isFilterOpen && (
                <CommonDialog noPadding>
                    <div className={styles.dialogContainer}>
                        <div className={styles.dialogHeader}>
                            <h2 className={styles.dialogTitle}>Select Date Range</h2>
                            <button className={styles.closeButton} onClick={() => setIsFilterOpen(false)}>
                                ×
                            </button>
                        </div>
                        <div className={styles.dateRangeWrapper}>
                            <DateRangePicker
                                ranges={[dateRange]}
                                onChange={(ranges: any) => setDateRange(ranges.selection)}
                                moveRangeOnFirstSelection={false}
                                months={1}
                                direction="horizontal"
                                maxDate={maxDate}
                                showDateDisplay={false}
                                rangeColors={["#42577A"]}
                            />
                        </div>
                        <div className={styles.dialogActions}>
                            <Button text="Reset" variant="outline" onClick={handleResetFilter} />
                            <Button text="Apply Filter" onClick={handleApplyFilter} />
                        </div>
                    </div>
                </CommonDialog>
            )}

            <DataTable<StaffReferral>
                data={data?.items ?? []}
                columns={columns}
                loading={isLoading}
                isUpdating={isFetching && !isLoading}
                headerActions={headerActions}
                onRowClick={(record) => navigate(record.id)}
                emptyComponent={
                    <EmptyState
                        icon={<SystemReferralMenuIconActive />}
                        title="No referrals found"
                    />
                }
            />

            <TablePagination
                isLoading={isFetching}
                totalItems={data?.count ?? 0}
                itemsPerPage={PAGE_SIZE}
                currentPage={page}
                onPageChange={setPage}
            />
        </>
    );
};
